// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component, createRef } from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { withRouter } from 'react-router-dom'
import qs from 'query-string'
import moment from 'moment'

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faHandHoldingUsd } from '@fortawesome/free-solid-svg-icons'

import {
  FilterSidebar,
  FilterPicklistWithData,
  FilterWithdrawalProduct,
  FilterComboEmployerDivision,
  FilterDateRange,
  Withdrawals,
  WithdrawalAddEditModal as AddModal,
  Modal,
  IdentityNumberFinder,
  Button,
  SvgIcon
} from '../components'

import {
  hasDateChanged,
  hasResponsibility
} from '../utils'

import { MUTATION_WITHDRAWAL } from '../mutations'

const modalRef = createRef()


class WithdrawalList extends Component {
  static propTypes = {
    identityId: PropTypes.string
  }

  state = {
    productFilter: '',
    employerFilter: '',
    divisionFilter: '',
    statusFilter: this.props.identityId
      ? 'pending'  // default status filter to 'pending' for identity-specific withdrawal listing
      : '',
    initiateOnStartDate: null,
    initiateOnEndDate: null,
    search: '',
    orderBy: 'initiate_on',
    currentActiveTab: 0,
    showAddModal: false,
    showModalErrorMessage: null,  // if set to string value, the value will be displayed in modal
    newWithdrawalClient: null
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutsideModal)

    // this life-cycle function implementation ensures that, on first render and/or
    // returning from another page, that filters will still be set to url and what it was before
    if (this.props.location.search) {
      this.setFiltersFromQueryString(this.props.location.search)
    } else {
      this.setQueryString(this.state)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutsideModal)
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search) {
      this.setFiltersFromQueryString(nextProps.location.search)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.location.search === '' &&
      (
        nextState.search !== '' ||
        nextState.productFilter !== '' ||
        nextState.employerFilter !== '' ||
        nextState.divisionFilter !== '' ||
        nextState.status !== '' ||
        nextState.initiateOnStartDate ||
        nextState.initiateOnEndDate ||
        nextState.orderBy !== ''
      )
    ) {
      this.setQueryString(nextState)
      return false
    }
    else if (Object.is(this.props.location.search, nextProps.location.search) && (
      this.state.search !== nextState.search ||
      this.state.productFilter !== nextState.productFilter ||
      this.state.employerFilter !== nextState.employerFilter ||
      this.state.divisionFilter !== nextState.divisionFilter ||
      this.state.statusFilter !== nextState.statusFilter ||
      hasDateChanged(this.state.initiateOnStartDate, nextState.initiateOnStartDate) ||
      this.state.orderBy !== nextState.orderBy
    )) {
      this.setQueryString(nextState)
      return false
    }
    else if (this.state.search !== nextState.search ||
      this.state.productFilter !== nextState.productFilter ||
      this.state.employerFilter !== nextState.employerFilter ||
      this.state.divisionFilter !== nextState.divisionFilter ||
      this.state.statusFilter !== nextState.statusFilter ||
      hasDateChanged(this.state.initiateOnStartDate, nextState.initiateOnStartDate) ||
      this.state.orderBy !== nextState.orderBy
    ) {
      this.setQueryString(nextState)
      return false
    }

    return true
  }

  handleClickOutsideModal = (e) => {
    if (this.state.showAddModal && modalRef.current && !modalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showAddModal: false })
    }
    if (this.state.showModalErrorMessage && modalRef.current && !modalRef.current.contains(e.target)) {
      // click is outside, close modal
      this.setState({ showModalErrorMessage: false })
    }
  }

  setFiltersFromQueryString = (queryString) => {
    let queryParams = qs.parse(queryString)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        [ 'search', 'orderBy' ].indexOf(key) > -1
          ? { [key]: queryParams[key] }
          : [  'initiateOnStartDate', 'initiateOnEndDate' ].indexOf(key) > -1  // end/startDate query params aren't translated into state variables with -'Filter' appended like the others
            ? { [ key ]: moment(queryParams[key], 'YYYYMMDDT') }
            : { [`${key}Filter`]: queryParams[key] }
      ))
    )

    this.setState(filters)
  }

  setQueryString = (state) => {
    let filters = {
      orderBy: state.orderBy
    }

    if (state.productFilter !== '') {
      filters.product = state.productFilter
    }

    if (state.employerFilter !== '') {
      filters.employer = state.employerFilter
    }
    if (state.divisionFilter !== '') {
      filters.division = state.divisionFilter
    }

    if (state.statusFilter !== '') {
      filters.status = state.statusFilter
    }

    if (state.search !== '') {
      filters.search = state.search
    }

    if (state.initiateOnStartDate) {
      filters.initiateOnStartDate = state.initiateOnStartDate.format('YYYYMMDD')
    }
    if (state.initiateOnEndDate) {
      filters.initiateOnEndDate = state.initiateOnEndDate.format('YYYYMMDD')
    }

    if (Object.keys(filters).length > 0) {
      if (this.props.identityId) {
        this.props.history.push({
          pathname: `/os/identity/${this.props.identityId}/products/withdrawals`,
          search: qs.stringify(filters)
        })
      } else {
        this.props.history.push({
          pathname: '/os/products/withdrawals',
          search: qs.stringify(filters)
        })
      }
    }
  }

  selectTab = (tabIndex) => this.setState({ currentActiveTab: tabIndex })

  toggleAddModal = () => (
    this.setState(prevState => (
      { showAddModal: !prevState.showAddModal }
    ))
  )

  toggleErrorModal = (message) => {
    if (message) {
      this.setState({ showModalErrorMessage: message })
    }
    else {
      this.setState({ showModalErrorMessage: null })
    }
  }

  setNewWithdrawalClient = (clientObj) => (
    this.setState({ newWithdrawalClient: clientObj })
  )

  saveNewWithdrawal = (withdrawalMutate, accountId, clientId, targetType, targetId, initiateOn, amountRequested, amountType, productMetaDataObj) => {
    let input = {
      client: clientId,
      account: accountId,
      initiateOn,
      amountRequested,
      status: 'pending',
      amountType
     }

    if (targetType === 'product') {
      input.targetProduct = targetId
    }

    if (targetType === 'bankaccount') {
      input.targetBankaccount = targetId
    }

    if (productMetaDataObj) {
      input.metadata = JSON.stringify(productMetaDataObj)
    }

    withdrawalMutate({ variables: { input } })
    .then(() => {
      this.toggleAddModal()
      this.setState({ listingRefreshTimestamp: moment.unix() })
    })
    .catch(this.handleWithdrawalSaveFail)
  }

  handleWithdrawalSaveFail = (error) => {
    this.toggleAddModal()
    if (error.hasOwnProperty('graphQLErrors') && error.graphQLErrors.length > 0) {
      this.toggleErrorModal(error.graphQLErrors[0].message)
    }
  }

  render() {
    let filters = [
      <FilterWithdrawalProduct
        value={this.state.productFilter}
        onChange={product => (
          this.setState({
            productFilter: product === 'All' ? '' : product
          }, () => this.setQueryString(this.state))
        )}
        useFieldValue={true}
        key='filterProduct'
      />,
      <FilterComboEmployerDivision
        employer={this.state.employerFilter}
        division={this.state.divisionFilter}
        onEmployerChange={employer => (
          this.setState({
            employerFilter: employer === 'All' ? '' : employer,
            divisionFilter: ''
          }, () => this.setQueryString(this.state))
        )}
        onDivisionChange={division => (
          this.setState({
            divisionFilter: division === 'All' ? '' : division
          }, () => this.setQueryString(this.state))
        )}
        key='filterComboEmployerDivision'
      />,
      <FilterPicklistWithData
        id='status'
        title='Status'
        forField='WithdrawalStatus'
        default='All'
        symbol='chevrondown'
        value={this.state.statusFilter}
        onChange={status => this.setState({ statusFilter: status }, () => this.setQueryString(this.state))}
        key='filterStatus'
      />,
      <FilterDateRange
        title='Initiate on between'
        startDate={this.state.initiateOnStartDate}
        endDate={this.state.initiateOnEndDate}
        onStartDateChange={date => this.setState({ initiateOnStartDate: date }, () => this.setQueryString(this.state))}
        onEndDateChange={date => this.setState({ initiateOnEndDate: date }, () => this.setQueryString(this.state))}
        startDatePlaceholder='From'
        endDatePlaceholder='To'
        dateFormat='DD MMM YYYY'
        showYearDropdown={true}
        showMonthDropdown={true}
        key='initiateOnBetweenFilter'
      />
    ]

    if (this.props.identityId) {
      filters.splice(1,1)  // removes FilterComboEmployerDivision
    }

    const { productFilter, employerFilter, divisionFilter, statusFilter, initiateOnStartDate,
      initiateOnEndDate, search, orderBy, showAddModal, newWithdrawalClient, listingRefreshTimestamp,
      showModalErrorMessage } = this.state

    const userMayOverrideDate = hasResponsibility('self', 'override_withdrawal_date')

    return (
      <div className="slds-grow slds-scrollable_y" role="main">
        <div className="slds-grid slds-wrap slds-size_1-of-1">
          <div className="tab-nav slds-size_1-of-1 u-bg-color-white product-listing-page-header">
            <div
              className="u-font-size-x-large u-text-caps slds-p-left_small product-listing-page-header_title"
              style={{ alignItems: "center" }}
            >
              <div
                className="slds-text-color_weak"
                style={{ padding: ".25em .75em" }}
              >
                <Icon icon={faHandHoldingUsd} size="2x" />
              </div>
              <div>Withdrawals</div>
            </div>
          </div>
          <FilterSidebar
            actions={
              <Mutation mutation={MUTATION_WITHDRAWAL}>
                {(withdrawalMutate) => (
                  <Button onClick={this.toggleAddModal}>Add withdrawal</Button>
                )}
              </Mutation>
            }
            filters={filters}
            filtersApplied={
              [
                productFilter,
                employerFilter,
                divisionFilter,
                statusFilter,
                initiateOnStartDate ? initiateOnStartDate.toString() : "",
                initiateOnEndDate ? initiateOnEndDate.toString() : "",
              ].filter((filter) => filter.length > 0).length
            }
            onClear={() =>
              this.setState({
                productFilter: "",
                employerFilter: "",
                divisionFilter: "",
                statusFilter: "",
                initiateOnStartDate: null,
                initiateOnEndDate: null,
              })
            }
            search={!this.props.identityId}
            searchString={this.state.search}
            searchPlaceholder="Search by ID"
            onSearchClick={(search) => this.setState({ search })}
            onSearchClear={() => this.setState({ search: "" })}
            componentFilters={true}
          />
          <div className="slds-col slds-size_10-of-12">
            <Withdrawals
              identityId={this.props.identityId}
              productName={productFilter}
              employerId={employerFilter}
              divisionId={divisionFilter}
              status={statusFilter === "all" ? "" : statusFilter}
              search={search}
              initiateOnStartDate={
                initiateOnStartDate
                  ? initiateOnStartDate.format("YYYY-MM-DD")
                  : undefined
              }
              initiateOnEndDate={
                initiateOnEndDate
                  ? initiateOnEndDate.format("YYYY-MM-DD")
                  : undefined
              }
              orderBy={orderBy}
              changeSort={(orderBy) => this.setState({ orderBy })}
              key={listingRefreshTimestamp}
            />
          </div>
        </div>
        {showAddModal ? (
          this.props.identityId ? (
            <Query
              query={gql`
                query ($id: ID!) {
                  identity(id: $id) {
                    id
                    client {
                      id
                      withdrawableAccounts
                      withdrawableTargetProducts
                      withdrawableTargetBankaccounts
                    }
                  }
                }
              `}
              variables={{
                id: this.props.identityId,
              }}
            >
              {({ loading, error, data }) => {
                if (loading || error) {
                  return null; // TODO: handle loading & error and render something appropriate
                }

                const client = data.identity.client;
                return (
                  <Mutation mutation={MUTATION_WITHDRAWAL}>
                    {(withdrawalMutate) => (
                      <AddModal
                        title="Add withdrawal"
                        withdrawableAccounts={client.withdrawableAccounts}
                        withdrawableTargetProducts={
                          client.withdrawableTargetProducts
                        }
                        withdrawableTargetBankaccounts={
                          client.withdrawableTargetBankaccounts
                        }
                        userMayOverrideDate={userMayOverrideDate}
                        onCancelClick={this.toggleAddModal}
                        onSaveClick={(
                          accountId,
                          targetType,
                          targetId,
                          initiateOn,
                          amountRequested,
                          amountType,
                          productMetaDataObj
                        ) => {
                          this.saveNewWithdrawal(
                            withdrawalMutate,
                            accountId,
                            client.id,
                            targetType,
                            targetId,
                            initiateOn,
                            amountRequested,
                            amountType,
                            productMetaDataObj
                          );
                        }}
                      />
                    )}
                  </Mutation>
                );
              }}
            </Query>
          ) : newWithdrawalClient === null ? (
            <Modal
              ref={modalRef}
              heading="Add withdrawal"
              body={
                <IdentityNumberFinder
                  label={
                    <span>
                      Search for client by ID or passport number
                      <span className="slds-p-left_x-small slds-text-body_small slds-text-color_weak">
                        (provide all of the identity number)
                      </span>
                    </span>
                  }
                  onCreateCaseClick={() => null}
                  numberFoundAdditionalRender={(identity) => (
                    <Button
                      type="brand"
                      minWidth={250}
                      onClick={() =>
                        this.setNewWithdrawalClient(identity.client)
                      }
                      className="slds-m-top_small slds-m-bottom_small"
                    >
                      Confirm & Proceed
                    </Button>
                  )}
                  className="slds-m-left_medium"
                />
              }
            />
          ) : (
            <Mutation mutation={MUTATION_WITHDRAWAL}>
              {(withdrawalMutate) => (
                <AddModal
                  title="Add withdrawal"
                  withdrawableAccounts={
                    newWithdrawalClient.withdrawableAccounts
                  }
                  withdrawableTargetProducts={
                    newWithdrawalClient.withdrawableTargetProducts
                  }
                  withdrawableTargetBankaccounts={
                    newWithdrawalClient.withdrawableTargetBankaccounts
                  }
                  userMayOverrideDate={userMayOverrideDate}
                  onCancelClick={this.toggleAddModal}
                  onSaveClick={(
                    accountId,
                    targetType,
                    targetId,
                    initiateOn,
                    amountRequested,
                    amountType,
                    productMetaDataObj
                  ) => {
                    this.saveNewWithdrawal(
                      withdrawalMutate,
                      accountId,
                      this.state.newWithdrawalClient.id,
                      targetType,
                      targetId,
                      initiateOn,
                      amountRequested,
                      amountType,
                      productMetaDataObj
                    );
                  }}
                />
              )}
            </Mutation>
          )
        ) : null}
        {showModalErrorMessage && (
          <Modal
            ref={modalRef}
            body={
              <div className="slds-text-align_center">
                <div>
                  <SvgIcon
                    variant="utility"
                    symbol="warning"
                    colour="error"
                    size="medium"
                  />
                </div>
                <p className="slds-m-top_medium slds-p-around_small u-font-size-larger">
                  {/*
                    the returned error string is in the form of "'[message]'", so we'll just
                    grab the actual string we want displayed, without the brackets and single quotes
                  */}
                  {showModalErrorMessage.substring(
                    2,
                    showModalErrorMessage.length - 2
                  )}
                </p>
              </div>
            }
          />
        )}
      </div>
    );
  }
}

export default withRouter(WithdrawalList)



// WEBPACK FOOTER //
// ./src/containers/WithdrawalList.js